import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  useTheme,
} from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from '@mui/icons-material/Edit';
import React, { useEffect, useState } from 'react';
import { ADD_LOCATION_DETAILS } from '../../helpers/graphql/locations';
import { getRoles } from '../../helpers/token';
import FeaturedImageUpload from '../Editor/FeaturedImageUpload';
import LinearProgress from '@mui/material/LinearProgress';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { red } from '@mui/material/colors';

const redbtntheme = createTheme({
  palette: {
    primary: red,
  },
});

const EditLocationDetails = props => {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState(undefined);
  const [attribution, setAttribution] = useState('');
  const [description, setDescription] = useState('');
  const [coverAlt, setCoverAlt] = useState('');
  const [body, setBody] = useState('');
  const [budgetScore, setBudgetScore] = useState(undefined);
  const [title, setTitle] = useState(props.city || props.subdivision || '');
  const [isFeatured, setFeatured] = useState(false);
  const [roles, setRoles] = useState(undefined);
  const [topics, setTopics] = useState(undefined);
  const [inProgress, setInProgress] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [images, setImages] = useState([]);

  useEffect(() => {
    setRoles(getRoles());
    if (props.data.image)
      setImage(
        props.data.image.replace(/&w=[0-9].*&h=[0-9]*/, '&w=2880&h=900'),
      );
    if (props.data.attribution) setAttribution(props.data.attribution);
    if (props.data.body) setBody(props.data.body);
    if (props.data.description) setDescription(props.data.description);
    if (props.data.coverAlt) setCoverAlt(props.data.coverAlt);
    if (props.data.budgetScore) setBudgetScore(props.data.budgetScore);
    if (props.data.title) setTitle(props.data.title);
    if (props.data.isFeatured !== undefined) setFeatured(props.data.isFeatured);
    if (props.data.topics !== undefined) setTopics(props.data.topics);
    if (props.data.images && props.data.images.length > 0)
      setImages(props.data.images);
  }, [props]);

  const [addLocationDetails] = useMutation(ADD_LOCATION_DETAILS);

  const { enqueueSnackbar } = useSnackbar();

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      enqueueSnackbar(notification.message, { variant });
    }
  };

  function handleLocationDetailsUpdate(isGenerateTravelGuide) {
    setInProgress(true);
    addLocationDetails({
      variables: {
        locationDetails: {
          countryCode: props.countryCode,
          subdivision: props.subdivision,
          city: props.city,
          tag: props.tag,
          image,
          coverAlt,
          attribution,
          description,
          budgetScore,
          title,
          isFeatured,
          topics,
          generateTravelGuide: isGenerateTravelGuide === true,
          images,
          body,
          // itinerary1Prompt,
          // itinerary2Prompt,
          // itinerary3Prompt,
        },
      },
    })
      .then(({ data }) => {
        if (data.addLocationDetails && data.addLocationDetails.success)
          setIsUpdated(true);
        newNotification(data.addLocationDetails);
        setInProgress(false);
      })
      .catch(err => {
        console.log(err);
        newNotification({
          success: false,
          message: 'Generation failed. Please try again',
        });
        setInProgress(false);
      });
  }

  function handelAiGeneration() {
    handleLocationDetailsUpdate(true);
  }

  function setImage1(url) {
    const newImages = [...images];
    newImages[0].url = url;
    setImages(newImages);
  }
  function setImage2(url) {
    const newImages = [...images];
    newImages[1].url = url;
    setImages(newImages);
  }
  function setAlt1(alt) {
    const newImages = [...images];
    newImages[0].alt = alt;
    setImages(newImages);
  }
  function setAlt2(alt) {
    const newImages = [...images];
    newImages[1].alt = alt;
    setImages(newImages);
  }

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isTopicsInvalid = () => {
    try {
      JSON.parse(topics);
      return false;
    } catch {
      return true;
    }
  };

  const isTeam = roles && roles.indexOf('team') !== -1;

  if (!isTeam) return <></>;

  return (
    <>
      <span className={props.triggerClassName || 'text-light'}>
        <IconButton
          color="inherit"
          onClick={handleClickOpen}
          aria-label="Edit"
          size={props.triggerClassName ? 'small' : 'large'}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </span>
      <Dialog
        fullWidth
        maxWidth="lg"
        disableEscapeKeyDown
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">Edit details</DialogTitle>
        {inProgress ? (
          <>
            <LinearProgress />
          </>
        ) : (
          (!isUpdated && (
            <>
              <DialogContent>
                <TextField
                  variant="standard"
                  label="Title"
                  fullWidth
                  value={title}
                  onChange={val => setTitle(val.target.value)}
                />
                <TextField
                  variant="standard"
                  label="Description"
                  fullWidth
                  multiline
                  value={description}
                  onChange={val => setDescription(val.target.value)}
                />
                <FormControlLabel
                  labelPlacement="end"
                  control={
                    <Switch
                      checked={isFeatured}
                      onChange={event => setFeatured(event.target.checked)}
                      color="primary"
                    />
                  }
                  label="Featured"
                />
                <FeaturedImageUpload
                  allowFullSize
                  featuredImage={image}
                  setFeaturedImage={input => setImage(input)}
                  placeholder="To upload a cover image, drag 'n' drop an image here, or click to select one. Recommended dimensions: 2880x900"
                />
                <TextField
                  variant="standard"
                  label="Cover Image Alt"
                  fullWidth
                  value={coverAlt}
                  onChange={val => setCoverAlt(val.target.value)}
                />
                <div className="pt-2" />
                <TextField
                  variant="standard"
                  label="Image Attribution"
                  fullWidth
                  value={attribution}
                  onChange={val => setAttribution(val.target.value)}
                />
                {!props.tag && (
                  <>
                    <InputLabel htmlFor="budget-score-helper">
                      Budget score
                    </InputLabel>
                    <Select
                      variant="standard"
                      value={budgetScore}
                      onChange={event => setBudgetScore(event.target.value)}
                      inputProps={{
                        name: 'budget-score',
                        id: 'budget-score-helper',
                      }}
                    >
                      <MenuItem value={1}>Very cheap</MenuItem>
                      <MenuItem value={2}>Cheap</MenuItem>
                      <MenuItem value={3}>Regular</MenuItem>
                      <MenuItem value={4}>Expensive</MenuItem>
                      <MenuItem value={5}>Very expensive</MenuItem>
                    </Select>
                    <TextField
                      variant="standard"
                      label="Topics"
                      fullWidth
                      value={topics}
                      onChange={val => setTopics(val.target.value)}
                    />
                  </>
                )}
                <div className="pt-2" />
                <FeaturedImageUpload
                  allowFullSize
                  featuredImage={
                    images && images.length > 0 ? images[0].url : ''
                  }
                  setFeaturedImage={input => setImage1(input)}
                  placeholder="Image 1"
                />
                <TextField
                  variant="standard"
                  label="Image1 Alt"
                  fullWidth
                  value={images && images.length > 0 ? images[0].alt : ''}
                  onChange={val => setAlt1(val.target.value)}
                />
                <div className="pt-2" />
                <FeaturedImageUpload
                  allowFullSize
                  featuredImage={
                    images && images.length > 1 ? images[1].url : ''
                  }
                  setFeaturedImage={input => setImage2(input)}
                  placeholder="Image 2"
                />
                <TextField
                  variant="standard"
                  label="Image2 Alt"
                  fullWidth
                  value={images && images.length > 1 ? images[1].alt : ''}
                  onChange={val => setAlt2(val.target.value)}
                />
                <div className="pt-2" />
                <TextField
                  variant="standard"
                  label="What's unique"
                  fullWidth
                  value={body}
                  onChange={val => setBody(val.target.value)}
                  multiline
                />
              </DialogContent>
              <DialogActions>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={redbtntheme}>
                    <Button onClick={handleClose} color="primary">
                      Cancel
                    </Button>
                  </ThemeProvider>
                </StyledEngineProvider>
                {props.countryCode &&
                  !props.subdivision &&
                  !props.city &&
                  !props.tag &&
                  (!props.data ||
                    !props.data.currencies ||
                    props.data.currencies.length === 0) && (
                    <Button
                      onClick={handelAiGeneration}
                      color="primary"
                      autoFocus
                      variant="outlined"
                      disabled={props.tag ? false : isTopicsInvalid()}
                    >
                      AI generate
                    </Button>
                  )}
                <Button
                  onClick={handleLocationDetailsUpdate}
                  color="primary"
                  autoFocus
                  variant="contained"
                  disabled={props.tag ? false : isTopicsInvalid()}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </>
          )) || (
            <>
              <DialogContent>
                Successfully updated! Reload to see the changes in action.
              </DialogContent>

              <DialogActions>
                <Button
                  // eslint-disable-next-line no-restricted-globals
                  onClick={() => location.reload()}
                  color="primary"
                  variant="contained"
                >
                  Reload
                </Button>
              </DialogActions>
            </>
          )
        )}
      </Dialog>
    </>
  );
};

export default EditLocationDetails;
